import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Calendar, ArrowRight, Clock, BookOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import MobileCTABar from "@/components/site/MobileCTABar";
import {
  PUBLISHED_POSTS,
  UPCOMING_POSTS,
  formatPostDate,
  getPostImage,
} from "@/lib/blog-posts";

function PostCard({ post, featured = false }) {
  const img = getPostImage(post);
  return (
    <article
      data-testid={`blog-card-${post.slug}`}
      className={[
        "group rounded-3xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl transition-all duration-300",
        featured ? "lg:grid lg:grid-cols-2 lg:gap-0" : "",
      ].join(" ")}
    >
      <Link to={`/blog/${post.slug}`} className="block overflow-hidden">
        <div
          className={[
            "relative bg-slate-100 overflow-hidden",
            featured ? "aspect-[5/4] lg:aspect-auto lg:h-full" : "aspect-[4/3]",
          ].join(" ")}
        >
          {img ? (
            <img
              src={img}
              alt={post.title}
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-blue to-brand-navy grid place-items-center">
              <BookOpen className="h-14 w-14 text-white/80" />
            </div>
          )}
          <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-navy">
            {post.category}
          </span>
        </div>
      </Link>
      <div className="p-6 sm:p-7 flex flex-col">
        <div className="flex items-center gap-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" /> {formatPostDate(post.date)}
          </span>
          {post.readingMinutes && (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {post.readingMinutes} min read
            </span>
          )}
        </div>
        <h2
          className={[
            "mt-3 font-display font-bold text-brand-navy leading-tight",
            featured ? "text-2xl sm:text-3xl" : "text-xl",
          ].join(" ")}
        >
          <Link
            to={`/blog/${post.slug}`}
            className="hover:text-brand-blue transition-colors"
          >
            {post.title}
          </Link>
        </h2>
        <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed line-clamp-3">
          {post.excerpt}
        </p>
        <Link
          to={`/blog/${post.slug}`}
          data-testid={`blog-read-more-${post.slug}`}
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-brand-blue hover:text-brand-navy"
        >
          Read more <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </article>
  );
}

function UpcomingCard({ post }) {
  return (
    <div
      data-testid={`blog-upcoming-${post.slug}`}
      className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/70 p-5 flex flex-col gap-2"
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700 bg-amber-100 rounded-full px-2.5 py-1">
          <Calendar className="h-3 w-3" /> Coming {formatPostDate(post.date)}
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {post.category}
        </span>
      </div>
      <h3 className="mt-2 font-display text-lg font-bold text-brand-navy leading-snug">
        {post.title}
      </h3>
      <p className="text-sm text-slate-600 leading-relaxed">{post.excerpt}</p>
    </div>
  );
}

export default function Blog() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PUBLISHED_POSTS;
    return PUBLISHED_POSTS.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q),
    );
  }, [query]);

  const [featured, ...rest] = filtered;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <main className="pt-28 lg:pt-36 pb-24">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-blue">
            Oxford Journal
          </p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-navy leading-[1.05]">
            Garment care, tips & style,
            <span className="block text-brand-blue">from your Chandler experts.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Practical advice from 30+ years of cleaning clothes for East Valley
            families — stain removal, seasonal care, alterations, wedding gown
            preservation, and everything in between.
          </p>

          {/* Search */}
          <div className="mt-8 relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="blog-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search posts — try 'wedding', 'stain', 'soccer'…"
              className="pl-11 h-12 rounded-full bg-white border-slate-300"
            />
          </div>
        </section>

        {/* Posts grid */}
        <section
          data-testid="blog-list-section"
          className="max-w-7xl mx-auto px-5 sm:px-8 mt-14"
        >
          {filtered.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center">
              <p className="font-semibold text-brand-navy">
                No posts match "{query}". Try another keyword.
              </p>
            </div>
          ) : (
            <>
              {featured && (
                <div className="mb-10">
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-blue mb-4">
                    Featured
                  </p>
                  <PostCard post={featured} featured />
                </div>
              )}

              {rest.length > 0 && (
                <>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-blue mb-4">
                    Latest posts
                  </p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rest.map((p) => (
                      <PostCard key={p.slug} post={p} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </section>

        {/* Upcoming */}
        {UPCOMING_POSTS.length > 0 && (
          <section
            data-testid="blog-upcoming-section"
            className="max-w-7xl mx-auto px-5 sm:px-8 mt-20"
          >
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-700">
                  Coming up
                </p>
                <h2 className="mt-2 font-display text-2xl sm:text-3xl font-bold text-brand-navy">
                  Our monthly content calendar
                </h2>
              </div>
              <p className="text-sm text-slate-600 max-w-md">
                A new post every month. Bookmark this page — or call us if you
                want us to write about a topic you care about.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {UPCOMING_POSTS.map((p) => (
                <UpcomingCard key={p.slug} post={p} />
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20">
          <div className="rounded-3xl bg-gradient-to-br from-brand-navy to-brand-blue text-white p-10 lg:p-12 flex flex-col lg:flex-row items-center justify-between gap-6 shadow-2xl">
            <div className="max-w-xl">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-300">
                New customer welcome pack
              </p>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold leading-tight">
                Get $10 off your first pickup.
              </h2>
              <p className="mt-3 text-slate-200 leading-relaxed">
                Trying us for the first time? Claim your code in seconds and
                show it at your first pickup.
              </p>
            </div>
            <Button
              asChild
              data-testid="blog-cta-claim"
              className="rounded-full bg-amber-500 hover:bg-amber-400 text-brand-navy h-12 px-7 text-base font-bold gap-2 shadow-[0_14px_30px_-12px_rgba(245,158,11,0.7)]"
            >
              <Link to="/#schedule">
                Schedule a free pickup <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
      <MobileCTABar />
    </div>
  );
}
